import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { type AppMode, DEFAULT_LANDING_PAGE, LANDING_PAGE_VALUES } from '@/types/appMode';
import { orgRoleHasScope } from '@releve/shared';
import { useUserOrganizations } from '@/hooks/useUserOrganizations';
import { useIsSuperAdmin } from '@/lib/permissions';
import { effectiveScopeRole } from '@/hooks/useCurrentUserOrgRole';

// Landing pages for each mode
const modeLandingPages: Record<AppMode, string> = {
  accounting: 'accounting/overview',
  projects: 'projects',
  sustainability: 'impact',
};

/** Type-safe check that a string is a valid AppMode */
function isValidAppMode(value: string | null | undefined): value is AppMode {
  return typeof value === 'string' && LANDING_PAGE_VALUES.includes(value as AppMode);
}

/**
 * Redirects the root path to the appropriate org-scoped page based on user's preference.
 * Precedence: User profile preference > Organization default > 'accounting'.
 * A role without the `accounting` scope (project manager) lands on the
 * dashboard instead of the accounting overview.
 */
export function RootRedirect() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { organizations, loading: orgLoading } = useOrganization();
  const { data: memberships, isLoading: membershipsLoading } = useUserOrganizations();
  const { data: isSuperAdmin, isLoading: superAdminLoading } = useIsSuperAdmin();

  // Fetch user's landing page preference from profile
  const { data: userLandingPage, isLoading: profileLoading } = useQuery({
    queryKey: ['userLandingPagePref', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('default_landing_page')
        .eq('user_id', user.id)
        .single();
      if (error) {
        console.error('Error fetching landing page preference:', error);
        return null;
      }
      const value = data?.default_landing_page;
      return isValidAppMode(value) ? value : null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  useEffect(() => {
    if (orgLoading || profileLoading || membershipsLoading || superAdminLoading) return;

    if (organizations.length > 0) {
      const org = organizations[0];

      // Determine mode: user preference > org default > fallback
      const orgDefault = isValidAppMode(org.default_landing_page) ? org.default_landing_page : DEFAULT_LANDING_PAGE;
      const mode = userLandingPage ?? orgDefault;

      const role = memberships?.find((m) => m.id === org.id)?.user_role ?? null;
      const canAccounting = orgRoleHasScope(effectiveScopeRole(role, isSuperAdmin), 'accounting');
      const landingPage = mode === 'accounting' && !canAccounting ? 'dashboard' : modeLandingPages[mode];

      // Redirect to first org (starred ones are sorted first)
      navigate(`/org/${org.id}/${landingPage}`, { replace: true });
    } else {
      // No orgs - go to org select
      navigate('/org-select', { replace: true });
    }
  }, [orgLoading, profileLoading, membershipsLoading, superAdminLoading, organizations, memberships, isSuperAdmin, userLandingPage, navigate]);

  return (
    <div className="flex items-center justify-center h-screen w-full">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}
